/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("AIService", ["InitService", "ModelHelper", "DateTimeHelper", "IndexedDbService"], function (context, cls) {
  // IndexedDB stores/index backing conversation history (declared in IndexedDbService SCHEMA)
  const CONVERSATIONS_STORE = "conversations";
  const MESSAGES_STORE = "messages";
  const MESSAGES_BY_CONVERSATION = "byConversation";

  /**
   * AI service to manage multiple AI providers
   * @namespace gbc.AIService
   * @gbcService
   */
  class AIService /** @lends gbc.AIService */ {
    constructor() {
      this._providers = new Map();
      this._message = null;
      this._messageService = null;
    }

    /**
     * @inheritDoc
     */
    init() {
      for (const provider of cls.AIProviderBase.createRegisteredProviders()) {
        this.registerProvider(provider);
      }
    }

    /**
     * @inheritdoc
     */
    destroy() {
      for (const provider of this._providers.values()) {
        provider.destroy?.();
      }
      this._providers.clear();
      this._message = null;
      this._messageService = null;
    }

    /**
     * Returns true if the connected GAS advertises the "ai" server feature
     * @returns {boolean}
     */
    hasGasAiCapability() {
      const session = context.SessionService.getCurrent();
      if (!session) {
        return false;
      }
      return session.hasServerFeature("ai");
    }

    /**
     * Returns true if AI features are enabled in this project.
     * Master switch is the gbc-ai-enabled theme var; the GAS "ai" capability only drives
     * call routing (via GAS proxy vs direct), not the activation itself.
     * @returns {boolean}
     */
    isEnabled() {
      return context.ThemeService.getValue("gbc-ai-enabled") === true;
    }

    /**
     * Register an AI provider instance
     * @param {classes.AIProviderBase} provider
     */
    registerProvider(provider) {
      if (!provider || !provider.id) {
        console.warn("AIService: invalid provider registration", provider);
        return;
      }
      if (this._providers.has(provider.id)) {
        console.warn(`AIService: provider already registered (${provider.id})`);
        return;
      }
      this._providers.set(provider.id, provider);
    }

    /**
     * Return remote providers info for UI listing
     * @returns {Array<{id:string,label:string,location:string}>}
     */
    getRemoteProvidersInfo() {
      return Array.from(this._providers.values())
        .filter((p) => p.isAvailable())
        .map((p) => p.getInfo())
        .filter((info) => info.location === "remote");
    }

    /**
     * AI provider ids advertised by the connected GAS
     * Empty list when no session exists or the GAS does not advertise any.
     * @returns {string[]}
     */
    getGasProviders() {
      const session = context.SessionService.getCurrent();
      if (!session) {
        return [];
      }
      return session.getAiProviders();
    }

    /**
     * Current AI mode.
     * Forced to "remote" when the GAS handles AI (chrome-local would bypass the GAS proxy);
     * otherwise read from stored settings ("chrome-local" or "remote").
     * @returns {string}
     */
    getMode() {
      if (this.hasGasAiCapability()) {
        return "remote";
      }
      return gbc.StoredSettingsService.readGbcSettings("aiMode");
    }

    /**
     * Active provider id, read from the `gbc-ai-default-provider` theme var.
     * @returns {string}
     */
    getProvider() {
      return context.ThemeService.getValue("gbc-ai-default-provider");
    }

    /**
     * API key for remote providers
     * @returns {string|null}
     */
    getApiKey() {
      return gbc.StoredSettingsService.readGbcSettings("aiApiKey");
    }

    /**
     * Returns the provider configured in settings, or null
     * @returns {classes.AIProviderBase|null}
     * @private
     */
    _getConfiguredProvider() {
      const providerId = this.getProvider();
      if (!providerId) {
        return null;
      }
      const provider = this._providers.get(providerId);
      if (!provider) {
        return null;
      }
      if (!provider.isAvailable()) {
        return null;
      }
      return provider;
    }

    /**
     * Initializes the message widget used to display error messages
     * Does nothing if the widget is already initialized or no application is available
     * @private
     */
    _initMessageWidget() {
      if (this._message) {
        return;
      }

      const application = context.SessionService.getCurrent()?.getCurrentApplication();
      if (!application) {
        return;
      }

      const userInterfaceNode = application.getNode(0);
      const userInterfaceWidget = userInterfaceNode.getController().getWidget();
      const msgWidget = userInterfaceWidget.getMessageWidget();

      this._messageService = application.message;
      this._message = msgWidget;
    }

    /**
     * Send an error message to the UI
     * @param {string} message
     * @private
     */
    _sendErrorMessage(message) {
      this._initMessageWidget();

      if (this._message && this._messageService) {
        this._message.setText(message);
        this._message.setMessageKind("error");
        this._message.setMessageColor("white", context.ThemeService.getValue("mt-red-600"));
        this._message.setHidden(false);
        this._messageService.addMessage("ai", this._message);
        this._messageService.handlePositions();
      }
    }

    //#region Rewrite

    /**
     * Build form context (locale sentence + field list) from the widget's current form
     * Excludes the current widget from the field list
     * @param {classes.WidgetBase} contextWidget
     * @returns {{contextForm: Array<{name:string,value:string,title:string}>, contextLocale: string|null}}
     * @private
     */
    _collectFormContext(contextWidget) {
      if (!contextWidget) {
        return { contextForm: [], contextLocale: null };
      }

      const modelHelper = new cls.ModelHelper(contextWidget);
      const formNode = modelHelper.getAnchorNode()?.getAncestor("Form");
      if (!formNode) {
        return { contextForm: [], contextLocale: null };
      }

      const contextLocale = this._collectLocaleData(formNode.getApplication().getNode(0));

      const contextForm = [];
      for (const field of formNode.getDescendants("FormField")) {
        if (field.getWidget() === contextWidget) {
          continue;
        }
        const name = field.attribute("name");
        if (!name) {
          continue;
        }

        const widget = field.getWidget();
        contextForm.push({
          name,
          value: widget.getValue(),
          title: widget.getTitle()
        });
      }

      return { contextForm, contextLocale };
    }

    /**
     * Collect locale formatting metadata (date format, decimal/thousands separators) from the UserInterface node
     * @param {classes.NodeBase} uiNode - the UserInterface node (root node 0)
     * @returns {?Object}
     * @private
     */
    _collectLocaleData(uiNode) {
      if (!uiNode) {
        return null;
      }

      const data = {};

      const dbDate = uiNode.attribute("dbDate");
      if (dbDate) {
        const traditional = cls.DateTimeHelper.parseDbDateFormat(dbDate);
        if (traditional) {
          data.dateFormat = traditional;
        }
      }

      const decimalSeparator = uiNode.attribute("decimalSeparator");
      if (decimalSeparator) {
        data.decimalSeparator = decimalSeparator;
      }

      const thousandsSeparator = uiNode.attribute("thousandsSeparator");
      if (thousandsSeparator) {
        data.thousandsSeparator = thousandsSeparator;
      }

      return Object.keys(data).length === 0 ? null : data;
    }

    /**
     * Rewrite text with the resolved provider
     * @param {string} text
     * @param {Object} options
     * @returns {Promise<string|null>}
     */
    async rewrite(text, options = {}) {
      if (text === null || text === undefined) {
        return null;
      }

      let contextForm = null;
      let contextLocale = null;
      if (options?.contextWidget) {
        ({ contextForm, contextLocale } = this._collectFormContext(options.contextWidget));
      }

      if (options?.selfContext) {
        contextForm = contextForm ?? [];
        contextForm.unshift({ name: "document", title: "Full document context", value: options.selfContext });
      }

      const mode = this.getMode();
      let provider = null;

      if (mode === "chrome-local") {
        provider = this._providers.get("chrome-rewriter");
      } else {
        provider = this._getConfiguredProvider();
      }
      if (!provider) {
        console.warn(`AIService: configured provider not found (${this.getProvider()})`);
        return null;
      }

      if (!provider.capabilities?.rewrite) {
        console.warn(`AIService: provider does not support rewrite (${provider.id})`);
        return null;
      }

      const callOptions = {
        ...options,
        contextForm,
        contextLocale,
        apiKey: provider.location === "remote" ? this.getApiKey() : undefined
      };

      try {
        return await provider.rewrite(text, callOptions);
      } catch (e) {
        console.warn("AIService: rewrite failed", e);
        this._sendErrorMessage(gbc.I18NService.t("gwc.ai.rewriteFailed"));
        return null;
      }
    }

    //#endregion

    //#region Conversation

    /**
     * Returns a conversation (metadata + messages) by its local ID, or null
     * @param {string} localId
     * @returns {Promise<Object|null>}
     */
    async getConversation(localId) {
      if (!gbc.IndexedDbService.isAvailable()) {
        return null;
      }
      try {
        const record = await gbc.IndexedDbService.get(CONVERSATIONS_STORE, localId);
        if (!record) {
          return null;
        }
        const conversation = await this._loadConversation(record);
        return conversation;
      } catch (e) {
        console.warn("AIService: getConversation failed", e);
        return null;
      }
    }

    /**
     * Returns all stored conversations (metadata + messages)
     * @returns {Promise<Object[]>}
     */
    async listConversations() {
      if (!gbc.IndexedDbService.isAvailable()) {
        return [];
      }
      try {
        const records = await gbc.IndexedDbService.getAll(CONVERSATIONS_STORE);
        records.sort((a, b) => b.updatedAt - a.updatedAt);
        const conversations = [];
        for (const record of records) {
          conversations.push(await this._loadConversation(record));
        }
        return conversations;
      } catch (e) {
        console.warn("AIService: listConversations failed", e);
        return [];
      }
    }

    /**
     * Creates a new conversation with the configured provider
     * @param {Object} [options={}]
     * @returns {Promise<string|null>} localId of the created conversation, or null on failure
     */
    async createConversation(options = {}) {
      // Gate on the DB actually being open (not just present) so we never create a server-side
      // conversation we can't persist — which would orphan it.
      try {
        await gbc.IndexedDbService.ready();
      } catch (e) {
        console.warn("AIService: conversations require IndexedDB, which is unavailable", e);
        return null;
      }

      const provider = this._getConfiguredProvider();
      if (!provider) {
        console.warn(`AIService: configured provider not found (${this.getProvider()})`);
        return null;
      }

      if (!provider.capabilities?.conversation) {
        console.warn(`AIService: provider does not support conversations (${provider.id})`);
        return null;
      }

      const callOptions = {
        ...options,
        apiKey: this.getApiKey()
      };

      try {
        const providerData = await provider.createConversation(callOptions);
        if (!providerData) {
          return null;
        }

        const localId = crypto.randomUUID();
        const state = {
          localId,
          providerId: provider.id,
          providerData,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };

        await this._persistConversation(state);
        return localId;
      } catch (e) {
        console.warn("AIService: createConversation failed", e);
        this._sendErrorMessage(gbc.I18NService.t("gwc.ai.conversationFailed"));
        return null;
      }
    }

    /**
     * Sends a user message in an existing conversation
     * @param {string} localId
     * @param {string} userMessage
     * @param {Object} [options={}]
     * @returns {Promise<string|null>} assistant response, or null on failure
     */
    async sendMessage(localId, userMessage, options = {}) {
      if (!userMessage || typeof userMessage !== "string" || !userMessage.trim()) {
        return null;
      }

      const trimmedMessage = userMessage.trim();

      const state = await this.getConversation(localId);
      if (!state) {
        console.warn(`AIService: conversation not found (${localId})`);
        return null;
      }

      const provider = this._providers.get(state.providerId);
      if (!provider) {
        console.warn(`AIService: provider not found (${state.providerId})`);
        return null;
      }

      const callOptions = {
        ...options,
        apiKey: this.getApiKey()
      };

      let result = null;
      try {
        result = await provider.sendMessage(state, trimmedMessage, callOptions);
      } catch (e) {
        console.warn("AIService: sendMessage failed", e);
        this._sendErrorMessage(gbc.I18NService.t("gwc.ai.conversationFailed"));
        return null;
      }

      if (!result?.response) {
        return null;
      }

      // Provider succeeded — persist best-effort; never discard a valid response on a storage error
      // (returning null here would make the caller retry and duplicate the message server-side).
      try {
        const userEntry = { role: "user", content: trimmedMessage, timestamp: Date.now() };
        const assistantEntry = { role: "assistant", content: result.response, timestamp: Date.now() };
        await this._persistMessage(localId, userEntry);
        await this._persistMessage(localId, assistantEntry);
        await this._persistConversation({ ...state, providerData: result.providerData, updatedAt: Date.now() });
      } catch (e) {
        console.warn("AIService: failed to persist sent message", e);
      }

      return result.response;
    }

    /**
     * Deletes a conversation — server-side and from persistent storage
     * @param {string} localId
     * @returns {Promise<void>}
     */
    async deleteConversation(localId) {
      if (!gbc.IndexedDbService.isAvailable()) {
        return;
      }

      let record = null;
      try {
        record = await gbc.IndexedDbService.get(CONVERSATIONS_STORE, localId);
      } catch (e) {
        console.warn("AIService: deleteConversation failed", e);
        return;
      }
      if (!record) {
        return;
      }

      const provider = this._providers.get(record.providerId);

      try {
        await provider?.deleteConversation(record, { apiKey: this.getApiKey() });
      } catch (e) {
        console.warn("AIService: deleteConversation failed", e);
      }

      // Messages first, then the conversation record: if interrupted, the record remains as a
      // marker so a later deleteConversation can still clean up the orphaned messages.
      try {
        await gbc.IndexedDbService.deleteAllFromIndex(MESSAGES_STORE, MESSAGES_BY_CONVERSATION, localId);
        await gbc.IndexedDbService.delete(CONVERSATIONS_STORE, localId);
      } catch (e) {
        console.warn("AIService: deleteConversation cleanup failed", e);
      }
    }

    /**
     * Rebuild a full conversation state (metadata + messages) from a stored record
     * @param {Object} record
     * @returns {Promise<Object>}
     * @private
     */
    async _loadConversation(record) {
      const messages = await gbc.IndexedDbService.getAllFromIndex(MESSAGES_STORE, MESSAGES_BY_CONVERSATION, record.localId);
      return {
        ...record,
        messages: messages.map((message) => ({ role: message.role, content: message.content, timestamp: message.timestamp }))
      };
    }

    /**
     * Persist conversation metadata (messages are stored separately)
     * @param {Object} state
     * @returns {Promise<void>}
     * @private
     */
    async _persistConversation(state) {
      await gbc.IndexedDbService.put(CONVERSATIONS_STORE, {
        localId: state.localId,
        providerId: state.providerId,
        providerData: state.providerData,
        createdAt: state.createdAt,
        updatedAt: state.updatedAt
      });
    }

    /**
     * Persist one message of a conversation
     * @param {string} localId
     * @param {Object} message - { role, content, timestamp }
     * @returns {Promise<void>}
     * @private
     */
    async _persistMessage(localId, message) {
      await gbc.IndexedDbService.put(MESSAGES_STORE, {
        conversationId: localId,
        role: message.role,
        content: message.content,
        timestamp: message.timestamp
      });
    }

    //#endregion
  }

  gbc.AIService = new AIService();
  context.InitService.register(context.AIService);
});
